import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface Prepayment {
  id: string;
  customerName: string;
  customerPhone?: string;
  amount: string;
  paymentMethod: string;
  forWhat?: string;
  status: 'held' | 'fulfilled' | 'refunded';
  takenBy?: string;
  resolvedBy?: string;
  resolvedNote?: string;
  resolvedAt?: string;
  createdAt: string;
}

/**
 * Money taken now for bread collected later.
 *
 * Anyone on the till can take one. Only a manager can close it off, because
 * that is what releases the money. Cash prepayments stay in the drawer and
 * count toward expected cash until then.
 */
export default function PrepaymentsPanel({
  branchId,
  branchName,
  isManager,
}: {
  branchId: string;
  branchName?: string;
  isManager: boolean;
}) {
  const [list, setList] = useState<Prepayment[]>([]);
  const [outstanding, setOutstanding] = useState<{ count: number; total: string }>({ count: 0, total: '0' });
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('CASH');
  const [forWhat, setForWhat] = useState('');

  const flash = (m: string) => {
    setMsg(m);
    setErr('');
    setTimeout(() => setMsg(''), 4000);
  };
  const fail = (e: any, fallback: string) => {
    const d = e?.response?.data?.message;
    setErr(Array.isArray(d) ? d.join(', ') : d || fallback);
    setMsg('');
  };

  const load = async () => {
    if (!branchId) return;
    try {
      const [rows, out] = await Promise.all([
        apiClient.listPrepayments(branchId, showAll ? 'all' : 'held'),
        apiClient.outstandingPrepayments(branchId),
      ]);
      setList(Array.isArray(rows.data) ? rows.data : []);
      setOutstanding(out.data);
    } catch (e: any) {
      fail(e, 'Could not load prepayments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branchId, showAll]);

  const take = async () => {
    if (!name.trim() || !amount.trim()) {
      setErr('A prepayment needs a name and an amount.');
      return;
    }
    setBusy(true);
    try {
      const res = await apiClient.createPrepayment(branchId, {
        customerName: name.trim(),
        customerPhone: phone.trim() || undefined,
        amount: Number(amount).toFixed(2),
        paymentMethod: method,
        forWhat: forWhat.trim() || undefined,
      });
      flash(`R ${res.data.amount} taken from ${res.data.customerName}.`);
      setName('');
      setPhone('');
      setAmount('');
      setForWhat('');
      await load();
    } catch (e: any) {
      fail(e, 'Could not record that');
    } finally {
      setBusy(false);
    }
  };

  const resolve = async (p: Prepayment, outcome: 'fulfilled' | 'refunded') => {
    const verb = outcome === 'fulfilled' ? 'collected' : 'refunded';
    if (!confirm(`Mark ${p.customerName}'s R ${p.amount} as ${verb}?`)) return;
    setBusy(true);
    try {
      await apiClient.resolvePrepayment(p.id, outcome);
      flash(`${p.customerName} — ${verb}.`);
      await load();
    } catch (e: any) {
      fail(e, 'Could not close that off');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-gray-500 py-8">Loading…</div>;

  const field =
    'w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent';

  return (
    <div className="space-y-6">
      {msg && <div className="bg-green-50 text-green-800 rounded-lg px-4 py-3 text-sm font-medium">{msg}</div>}
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Owed to customers</p>
            <p className="text-3xl font-bold text-amber-900 tabular-nums mt-1">
              R {Number(outstanding.total).toFixed(2)}
            </p>
            <p className="text-sm text-amber-800 mt-1">
              {outstanding.count} order{outstanding.count === 1 ? '' : 's'} not collected yet
              {branchName ? ` at ${branchName}` : ''}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900">Take a prepayment</h3>
            <p className="text-sm text-gray-500 mt-1">Paying now, collecting later.</p>
            <div className="space-y-3 mt-5">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer name" className={field} />
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" inputMode="tel" className={field} />
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                <input
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  inputMode="decimal"
                  className={`${field} pl-8 tabular-nums`}
                />
              </div>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className={field}>
                <option value="CASH">Cash</option>
                <option value="CARD">Card</option>
                <option value="MOBILE">Mobile</option>
                <option value="TRANSFER">Transfer</option>
              </select>
              <input
                value={forWhat}
                onChange={(e) => setForWhat(e.target.value)}
                placeholder="What for, e.g. 10 brown loaves Sunday"
                className={field}
              />
              <button
                onClick={take}
                disabled={busy}
                className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold py-3 rounded-lg"
              >
                Take payment
              </button>
              {method === 'CASH' && (
                <p className="text-xs text-gray-500">
                  Cash stays in the drawer and counts at cash-up until it is collected.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow lg:col-span-2 overflow-hidden">
          <div className="px-6 py-4 border-b flex justify-between items-center gap-4">
            <h3 className="text-lg font-medium text-gray-900">
              {showAll ? 'All prepayments' : 'Waiting to be collected'}
            </h3>
            <label className="flex items-center gap-2 text-sm text-gray-600 shrink-0">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              Show closed
            </label>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td className="py-12 text-center text-gray-500">
                      {showAll ? 'Nothing recorded yet.' : 'Nobody is waiting on an order.'}
                    </td>
                  </tr>
                ) : (
                  list.map((p) => (
                    <tr key={p.id} className={`border-b last:border-0 ${p.status === 'held' ? '' : 'opacity-60'}`}>
                      <td className="py-3 px-6">
                        <p className="font-medium text-gray-900">{p.customerName}</p>
                        {p.forWhat && <p className="text-xs text-gray-600">{p.forWhat}</p>}
                        <p className="text-xs text-gray-500">
                          {p.customerPhone ? `${p.customerPhone} · ` : ''}
                          taken by {p.takenBy}
                          {p.status !== 'held' && p.resolvedBy ? ` · ${p.status} by ${p.resolvedBy}` : ''}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <p className="tabular-nums font-bold text-gray-900">R {Number(p.amount).toFixed(2)}</p>
                        <p className="text-xs text-gray-500 lowercase">{p.paymentMethod}</p>
                      </td>
                      <td className="py-3 px-6 text-right w-44">
                        {p.status === 'held' ? (
                          isManager ? (
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => resolve(p, 'fulfilled')}
                                disabled={busy}
                                className="text-xs bg-green-600 hover:bg-green-700 text-white px-2.5 py-1.5 rounded font-medium"
                              >
                                Collected
                              </button>
                              <button
                                onClick={() => resolve(p, 'refunded')}
                                disabled={busy}
                                className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-800 px-2.5 py-1.5 rounded font-medium"
                              >
                                Refund
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">waiting</span>
                          )
                        ) : (
                          <span className="text-xs text-gray-500 capitalize">{p.status}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
